import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from './icons';
import './toast.css';

/**
 * Toasts du design system (ADR-100, étendu à toute l'application) : un message furtif en bas de
 * l'écran, au lieu d'un bandeau qui pousse la page. Un succès ou une information s'efface seul
 * (4 s, minuterie suspendue au survol) ; une erreur reste jusqu'à ce qu'on la ferme — un refus du
 * serveur ne disparaît pas avant d'avoir été lu. Trois au plus à la fois ; région `aria-live`.
 * Présentation seulement : le serveur a déjà décidé.
 */

export type ToastTone = 'success' | 'info' | 'danger';
export type ToastFn = (tone: ToastTone, text: string) => void;

interface ToastEntry {
  id: number;
  tone: ToastTone;
  text: string;
}

const DISMISS_MS = 4000;
const ToastContext = createContext<ToastFn | null>(null);

/** Le toast de l'application ; sans fournisseur (page rendue seule), un appel est sans effet. */
export function useToast(): ToastFn {
  return useContext(ToastContext) ?? noop;
}
const noop: ToastFn = () => undefined;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const next = useRef(0);
  const dismiss = useCallback((id: number): void => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setToasts((current) => current.filter((entry) => entry.id !== id));
  }, []);
  const arm = useCallback((id: number): void => {
    clearTimeout(timers.current.get(id));
    timers.current.set(id, setTimeout(() => dismiss(id), DISMISS_MS));
  }, [dismiss]);
  const toast = useCallback<ToastFn>(
    (tone, text) => {
      next.current += 1;
      const id = next.current;
      setToasts((current) => [...current.slice(-2), { id, tone, text }]);
      if (tone !== 'danger') arm(id);
    },
    [arm],
  );
  const value = useMemo(() => toast, [toast]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="ay-toasts" aria-live="polite">
        {toasts.map((entry) => (
          <div
            key={entry.id}
            role={entry.tone === 'danger' ? 'alert' : 'status'}
            className={`ay-toast ay-toast--${entry.tone}`}
            onMouseEnter={() => clearTimeout(timers.current.get(entry.id))}
            onMouseLeave={() => entry.tone !== 'danger' && arm(entry.id)}
          >
            <Icon name={entry.tone === 'danger' ? 'alert' : entry.tone === 'info' ? 'dot' : 'check'} size={16} />
            <span>{entry.text}</span>
            <button type="button" className="ay-toast__close" aria-label="Fermer" onClick={() => dismiss(entry.id)}>
              <Icon name="close" size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
